/**
 * SIM-1724 — Skills section of the per-tenant Dottie context editor, refactored from row+Sheet CRUD
 * into an inline editor with a single atomic Save and a required Reason. Every create, update and
 * delete is staged in a tab-local reducer; nothing reaches the server until Save flushes the batch
 * with one shared reason. Skills only — other tabs keep per-row hooks.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  QueryView,
} from "@simplicity/ui";
import { useReducer, useState } from "react";
import { notifyMutationError, notifyWarnings } from "./notify";
import { SkillRow } from "./SkillRow";
import {
  hasStagedChanges,
  initialDraftState,
  mergeRows,
  skillsDraftReducer,
  toBatch,
  type DraftRow,
  type SkillFields,
} from "./skillsDraft";
import { SkillsSaveBar } from "./SkillsSaveBar";
import type { AgentSkill } from "./types";
import { useSaveAgentContext, useSkills } from "./useDottieContext";

interface SkillsSectionProps {
  tenantId: string;
}

let tempIdSeq = 0;
function nextTempId(): string {
  tempIdSeq += 1;
  return `new-${tempIdSeq}`;
}

interface RowsProps {
  rows: DraftRow[];
  expanded: string[];
  onToggleExpand: (key: string) => void;
  onChangeCreate: (tempId: string, patch: Partial<SkillFields>) => void;
  onChangeExisting: (row: DraftRow, patch: Partial<SkillFields>) => void;
  onRequestDelete: (row: DraftRow) => void;
  onUndoDelete: (id: string) => void;
  onRemoveCreate: (tempId: string) => void;
}

function SkillRows(props: RowsProps) {
  const { rows, expanded } = props;
  if (rows.length === 0) {
    return <EmptyState title="No skills yet" description="Add the first skill for this tenant." />;
  }
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <SkillRow
          key={row.key}
          row={row}
          expanded={expanded.includes(row.key)}
          onToggleExpand={() => props.onToggleExpand(row.key)}
          onChange={(patch) =>
            row.id ? props.onChangeExisting(row, patch) : props.onChangeCreate(row.key, patch)
          }
          onRequestDelete={() => props.onRequestDelete(row)}
          onUndoDelete={() => row.id && props.onUndoDelete(row.id)}
          onRemoveCreate={() => props.onRemoveCreate(row.key)}
        />
      ))}
    </ul>
  );
}

export function SkillsSection({ tenantId }: SkillsSectionProps) {
  const skillsQuery = useSkills(tenantId);
  const [draft, dispatch] = useReducer(skillsDraftReducer, initialDraftState);
  const [reason, setReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<DraftRow | null>(null);
  const save = useSaveAgentContext(tenantId);

  const onSave = () => {
    save.mutate(
      { tenantId, reason: reason.trim(), skills: toBatch(draft) },
      {
        onSuccess: (res) => {
          notifyWarnings(res.warnings);
          dispatch({ type: "reset" });
          setReason("");
        },
        onError: (err) => notifyMutationError("Save", err),
      },
    );
  };

  const confirmDelete = () => {
    if (deleteTarget?.id) dispatch({ type: "markDelete", id: deleteTarget.id });
    setDeleteTarget(null);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Skills</h2>
        <Button onClick={() => dispatch({ type: "startCreate", tempId: nextTempId() })}>
          Add skill
        </Button>
      </div>

      <QueryView
        query={skillsQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load skills: {err.message}
          </p>
        )}
      >
        {(server: AgentSkill[]) => (
          <SkillRows
            rows={mergeRows(server, draft)}
            expanded={draft.expanded}
            onToggleExpand={(key) => dispatch({ type: "toggleExpanded", key })}
            onChangeCreate={(tempId, patch) => dispatch({ type: "editCreate", tempId, patch })}
            onChangeExisting={(row, patch) =>
              dispatch({
                type: "editExisting",
                id: row.id as string,
                base: { name: row.name, description: row.description, body: row.body },
                patch,
              })
            }
            onRequestDelete={setDeleteTarget}
            onUndoDelete={(id) => dispatch({ type: "undoDelete", id })}
            onRemoveCreate={(tempId) => dispatch({ type: "removeCreate", tempId })}
          />
        )}
      </QueryView>

      <SkillsSaveBar
        reason={reason}
        onReasonChange={setReason}
        hasChanges={hasStagedChanges(draft)}
        pending={save.isPending}
        onSave={onSave}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete skill?</AlertDialogTitle>
            <AlertDialogDescription>
              This stages the skill for deletion. It is removed (with a revision recorded) only when
              you Save. You can Undo before saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
